import React, { useMemo } from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { GET_STOCK_DELIVERY_LIST } from '../../../common/graphql/stockDelivery';
import { ROLE_PERMISSIONS } from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import Text from '../../components/text/Text';
import { EmptyState } from '../../design/components';

import StockDeliveryList from './StockDeliveryList';

const StockDeliveryListScreen = ({ hideHeader = false }) => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { hasPermission } = useUserContext();

	const canAccessStockDeliveryManagement = useMemo(() => {
		return Boolean(hasPermission(ROLE_PERMISSIONS.VIEW_STOCK_DELIVERY_LIST_PAGE));
	}, [hasPermission]);

	const canViewStockDeliveryDetail = useMemo(() => {
		return Boolean(hasPermission(ROLE_PERMISSIONS.VIEW_STOCK_DELIVERY_DETAIL_PAGE));
	}, [hasPermission]);

	const canViewPurchaseOrderDetail = useMemo(() => {
		return Boolean(hasPermission(ROLE_PERMISSIONS.VIEW_PURCHASE_ORDER_DETAIL_PAGE));
	}, [hasPermission]);

	const handleOpenDetail = useMemo(() => {
		return (stockDelivery) => {
			if (stockDelivery?.id && canViewStockDeliveryDetail) {
				navigation.navigate('StockDeliveryDetailView', { id: stockDelivery.id });
			}
		};
	}, [canViewStockDeliveryDetail, navigation]);

	if (!canAccessStockDeliveryManagement) {
		return (
			<EmptyState icon="lock" title="You do not have access to stock deliveries." />
		);
	}

	return (
		<View style={styles.container}>
			{
				!hideHeader && (
					<View style={styles.header}>
						<Text style={styles.heading} accessibilityRole="header">Stock deliveries</Text>
						<Text style={styles.subheading}>Receive products and capture batches</Text>
					</View>
				)
			}
			<StockDeliveryList
				query={GET_STOCK_DELIVERY_LIST}
				initialSortBy="created"
				initialSortDirection={-1}
				initialPerPage={20}
				initialFilter={{}}
				listId="mobile_stock_delivery_list"
				customProps={{
					onPressItem: handleOpenDetail,
					canViewDetail: canViewStockDeliveryDetail,
					canViewPurchaseOrderDetail,
				}}
			/>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	container: {
		flex: 1,
		backgroundColor: colors.canvas,
	},
	header: {
		paddingHorizontal: space.gutter,
		paddingTop: 20,
		paddingBottom: space.md,
		gap: 6,
	},
	heading: {
		...type.title,
		color: colors.text,
	},
	subheading: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
});

export default StockDeliveryListScreen;
